import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { BetterAuthError } from "../utils";
import { mcpFetch } from "./mcpFetch";
import type { TokenEndpointAuthMethod } from "./schemas";
import { MCP_CLIENTS_QUERY_KEY } from "./useMcpClients";

export type UpdateMcpClientAuthMethodInput = {
  clientId: string;
  tokenEndpointAuthMethod: TokenEndpointAuthMethod;
};

/**
 * Switches how a credential's secret is sent to the token endpoint. The client ID and secret are
 * unchanged — auth-service writes the method without regenerating the secret, which upstream would
 * otherwise do on this change.
 */
export function useUpdateMcpClientAuthMethod(
  options?: UseMutationOptions<unknown, BetterAuthError, UpdateMcpClientAuthMethodInput, unknown>,
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options ?? {};

  return useMutation({
    ...rest,
    mutationFn: async ({ clientId, tokenEndpointAuthMethod }: UpdateMcpClientAuthMethodInput) =>
      mcpFetch(
        "/oauth2/update-client",
        z.unknown(),
        {
          method: "POST",
          body: {
            client_id: clientId,
            update: { token_endpoint_auth_method: tokenEndpointAuthMethod },
          },
        },
        "Could not change the client authentication",
      ),
    onSuccess: async (...args) => {
      await queryClient.invalidateQueries({
        queryKey: MCP_CLIENTS_QUERY_KEY,
        refetchType: "all",
      });
      onSuccess?.(...args);
    },
  });
}
